import React, { useState } from "react";
import styled from "styled-components";
import { head } from "lodash";
import { FileInput } from "@sonder/ui/source/styled/components/FileInput";
import { Image } from "@sonder/ui/source/styled/components/Image";
import {
  Modal,
  ModalHeader,
  ModalProps,
} from "@sonder/ui/source/styled/components/Modal";
import { InputField } from "@sonder/ui/source/styled/fragments/InputField";
import { Label } from "@sonder/ui/source/styled/fragments/Label";
import { ModalFooter } from "@sonder/ui/source/styled/fragments/Modal";
import { Placeholder } from "@sonder/ui/source/styled/fragments/Placeholder";
import { PrimaryButton } from "@sonder/ui/source/styled/fragments/PrimaryButton";
import { DangerousValidationException } from "@sonder/ui/source/styled/fragments/ValidationException";
import { colors } from "@sonder/ui/source/styled/theme";
import { useSegment } from "analytics/segment";
import { ONE_MEGABYTE } from "core/constants";
import { useErrorReporterFiltered } from "core/hooks/useErrorReporterFiltered";
import { useCreateAttachment } from "core/queries/CreateAttachment";
import { TaskData } from "core/queries/GetTask";
import { UploadFileCategory, useUploadFiles } from "tasks/hooks/useUploadFiles";
import { Attachable, Format } from "typings/globalGQLTypes";

const SEGMENT_COMPONENT_NAME = "upload_task_photo";

const StyledImage = styled(Image)`
  margin-bottom: 1rem;
  width: 100%;
`;

const UploadPhotoField = styled(InputField)`
  display: flex;
`;

const StyledFileOptions = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
`;

const StyledFileInput = styled(FileInput)`
  align-items: center;
  border: 1px solid ${colors.blue7};
  border-radius: 0.3rem;
  color: ${colors.blue7};
  cursor: pointer;
  height: 2.3rem;
  margin-bottom: 0.5rem;
  padding: 0 0.7rem;
`;

interface UploadTaskPhotoModalProps
  extends Pick<ModalProps, "isOpen" | "onClose"> {
  maxPhotoUploadSize?: number;
  task: TaskData;
}

export const UploadTaskPhotoModal = ({
  isOpen,
  maxPhotoUploadSize = 4,
  onClose,
  task,
}: UploadTaskPhotoModalProps) => {
  const [imageValidationException, setImageValidationException] = useState("");
  const errorReporter = useErrorReporterFiltered();
  const { track } = useSegment();
  const taskId = task.id.split("|").pop();

  if (!taskId) return null;

  const [
    onCreateAttachment,
    { loading: isAttachmentCreating, error: createError },
  ] = useCreateAttachment({
    awaitRefetchQueries: true,
    refetchQueries: ["GetTaskComments"],
  });
  const {
    onUploadFiles,
    uploadState: { loading: isUploading, data: uploadData, error: uploadError },
  } = useUploadFiles();
  const image = head(uploadData?.uploadedFiles);
  const onCreate = async (callback: () => void) => {
    if (!taskId || !image?.publicId) return;

    await onCreateAttachment({
      variables: {
        targetId: taskId,
        targetType: Attachable.task,
        format: image.format as Format,
        publicId: image.publicId,
      },
    });

    if (callback) callback();
  };

  const onUpload = async ({
    target: { validity, files },
  }: React.ChangeEvent<HTMLInputElement>) => {
    try {
      const file = head(files);
      if (!taskId || !validity.valid || !file) return;
      // Reset upload error state
      setImageValidationException("");

      // Check for max file size
      if (file.size > ONE_MEGABYTE * maxPhotoUploadSize)
        throw new Error(`File size must be less than ${maxPhotoUploadSize}mb`);

      // Upload files
      await onUploadFiles({
        category: UploadFileCategory.TaskCommentAttachment,
        files: [file],
      });

      // Track successful upload
      track({
        eventName: SEGMENT_COMPONENT_NAME,
        action: "success",
        element: `${SEGMENT_COMPONENT_NAME}.photo_upload`,
        props: {
          taskId,
          nextValue: file.name,
        },
      });
    } catch (error) {
      errorReporter.notify(error);
      setImageValidationException(error.message);
      track({
        eventName: SEGMENT_COMPONENT_NAME,
        action: "failure",
        element: `${SEGMENT_COMPONENT_NAME}.photo_upload`,
        props: { taskId },
      });
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      {({ closeModal }) => (
        <>
          <ModalHeader title="Upload Photo for a Task" onClose={closeModal} />
          <UploadPhotoField>
            <Label>
              Valid File Types include: PDF, GIF, JPEG, WEBP, and PNG.
            </Label>
            {image && image.url && <StyledImage src={image.url} />}
            {!image && <Placeholder />}
            <StyledFileOptions>
              <StyledFileInput
                accept="image/png,image/jpeg,image/gif,image/webp,application/pdf"
                disabled={isUploading}
                name="image"
                onChange={onUpload}
              >
                {isUploading ? "Uploading..." : "Upload Photo"}
              </StyledFileInput>
            </StyledFileOptions>
            {imageValidationException && (
              <DangerousValidationException
                message={imageValidationException}
              />
            )}
          </UploadPhotoField>
          {createError && createError.message && (
            <DangerousValidationException message={createError.toString()} />
          )}
          {uploadError && uploadError.message && (
            <DangerousValidationException message={uploadError.toString()} />
          )}
          <ModalFooter>
            <PrimaryButton
              disabled={isAttachmentCreating || !image}
              type="submit"
              onClick={e => {
                e.preventDefault();
                onCreate(closeModal);
              }}
            >
              Submit
            </PrimaryButton>
          </ModalFooter>
        </>
      )}
    </Modal>
  );
};
